import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, Plus, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatPrice } from "@/lib/api";

const CONTENT_FIELDS = [
  ["hero_subtitle", "Hero — sottotitolo", "text"],
  ["hero_text", "Hero — testo breve", "textarea"],
  ["about_text", "Chi sono — testo", "textarea"],
  ["certifications", "Certificazioni (una per riga)", "list"],
  ["contact_email", "Email di contatto", "text"],
  ["instagram_url", "Link Instagram", "text"],
  ["whatsapp_url", "Link WhatsApp (opzionale)", "text"],
  ["legal_footer", "Dati legali nel footer (P.IVA, sede, ecc.)", "text"],
  ["coaching_intro", "Coaching — testo introduttivo", "textarea"],
  ["guide_audience", "Guida — a chi è rivolta", "textarea"],
];

const EMPTY_PRODUCT = {
  slug: "", type: "guide", name: "", subtitle: "", description: "",
  price_eur: "", cover_image: "", preview_images: "", features: "", contents: "",
  active: true, sort_order: 99,
};

function toForm(p) {
  return {
    slug: p.slug, type: p.type, name: p.name, subtitle: p.subtitle || "",
    description: p.description || "", price_eur: ((p.price_cents || 0) / 100).toString(),
    cover_image: p.cover_image || "", preview_images: (p.preview_images || []).join("\n"),
    features: (p.features || []).join("\n"), contents: (p.contents || []).join("\n"),
    active: p.active !== false, sort_order: p.sort_order ?? 99,
  };
}

function fromForm(f) {
  return {
    slug: f.slug, type: f.type, name: f.name, subtitle: f.subtitle, description: f.description,
    price_cents: Math.round(parseFloat(f.price_eur.replace(",", ".")) * 100) || 0,
    currency: "eur", cover_image: f.cover_image,
    preview_images: f.preview_images.split("\n").map((s) => s.trim()).filter(Boolean),
    features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
    contents: f.contents.split("\n").map((s) => s.trim()).filter(Boolean),
    active: f.active, sort_order: parseInt(f.sort_order, 10) || 99,
  };
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.isNew) {
        await api.post("/admin/products", fromForm(editing.form));
        toast.success("Prodotto creato");
      } else {
        await api.put(`/admin/products/${editing.slug}`, fromForm(editing.form));
        toast.success("Prodotto aggiornato");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, kind = "text") => (
    <div>
      <label className="label-caps mb-2 block text-ivory/60">{label}</label>
      {kind === "textarea" ? (
        <textarea rows={3} className="input-dark resize-y" data-testid={`product-field-${key}`}
          value={editing.form[key]} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, [key]: e.target.value } })} />
      ) : (
        <input className="input-dark" data-testid={`product-field-${key}`}
          value={editing.form[key]} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, [key]: e.target.value } })} />
      )}
    </div>
  );

  return (
    <div data-testid="admin-products-tab">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="font-display text-2xl text-ivory">Prodotti</h2>
        <button onClick={() => setEditing({ isNew: true, form: { ...EMPTY_PRODUCT } })} data-testid="product-new-button" className="btn-primary !px-5 !py-3">
          <Plus size={14} /> Nuovo prodotto
        </button>
      </div>

      {editing && (
        <div className="mb-10 border border-sage bg-ink p-8" data-testid="product-edit-form">
          <div className="mb-6 flex items-center justify-between">
            <p className="font-display text-xl text-ivory">{editing.isNew ? "Nuovo prodotto" : `Modifica: ${editing.slug}`}</p>
            <button onClick={() => setEditing(null)} data-testid="product-edit-close" className="text-mute hover:text-ivory"><X size={18} /></button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {editing.isNew && field("slug", "Slug (univoco, es. guida-nuova)")}
            {editing.isNew && (
              <div>
                <label className="label-caps mb-2 block text-ivory/60">Tipo</label>
                <select className="input-dark bg-ink" data-testid="product-field-type" value={editing.form.type}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, type: e.target.value } })}>
                  <option value="guide">Guida digitale</option>
                  <option value="coaching">Pacchetto coaching</option>
                </select>
              </div>
            )}
            {field("name", "Nome")}
            {field("price_eur", "Prezzo in € (es. 29.00)")}
            {field("subtitle", "Sottotitolo")}
            {field("cover_image", "Immagine copertina (URL o /images/...)")}
            {field("description", "Descrizione", "textarea")}
            {field("features", "Caratteristiche (una per riga)", "textarea")}
            {field("contents", "Contenuti guida (una per riga)", "textarea")}
            {field("preview_images", "Anteprime (URL, una per riga)", "textarea")}
            <div className="flex items-end gap-6 pb-2">
              <label className="flex items-center gap-3 text-sm text-ivory/80">
                <input type="checkbox" checked={editing.form.active} data-testid="product-field-active"
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, active: e.target.checked } })} />
                Attivo
              </label>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="product-save-button" className="btn-primary mt-8 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Salva"}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {products.map((p) => (
          <div key={p.slug} className="flex flex-wrap items-center justify-between gap-4 border border-line px-6 py-5" data-testid={`admin-product-${p.slug}`}>
            <div>
              <p className="font-display text-lg text-ivory">{p.name}</p>
              <p className="text-xs text-mute">{p.type} · {formatPrice(p.price_cents, p.currency)} · {p.active ? "attivo" : "nascosto"}</p>
            </div>
            <button onClick={() => setEditing({ isNew: false, slug: p.slug, form: toForm(p) })} data-testid={`product-edit-${p.slug}`} className="btn-outline !px-5 !py-2.5">
              <Pencil size={13} /> Modifica
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentTab() {
  const [content, setContent] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  if (!content) return <Loader2 className="animate-spin text-mist" size={24} />;

  const save = async (key, value) => {
    setSavingKey(key);
    try {
      await api.put(`/admin/content/${key}`, { value });
      toast.success("Salvato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="flex flex-col gap-8" data-testid="admin-content-tab">
      {CONTENT_FIELDS.map(([key, label, kind]) => {
        const raw = content[key];
        const isList = Array.isArray(raw);
        return (
          <div key={key} className="border border-line p-6">
            <label className="label-caps mb-3 block text-ivory/60">{label}</label>
            {kind === "textarea" || isList ? (
              <textarea rows={isList ? 3 : 4} className="input-dark resize-y" data-testid={`content-field-${key}`}
                value={isList ? raw.join("\n") : raw || ""}
                onChange={(e) => setContent({ ...content, [key]: isList ? e.target.value.split("\n") : e.target.value })} />
            ) : (
              <input className="input-dark" data-testid={`content-field-${key}`} value={raw || ""}
                onChange={(e) => setContent({ ...content, [key]: e.target.value })} />
            )}
            <button onClick={() => save(key, content[key])} disabled={savingKey === key} data-testid={`content-save-${key}`} className="btn-outline mt-4 !px-5 !py-2.5 disabled:opacity-60">
              {savingKey === key ? <Loader2 size={13} className="animate-spin" /> : "Salva"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-orders-tab" className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-line text-xs uppercase tracking-widest text-mute">
            <th className="py-3 pr-4">Data</th><th className="py-3 pr-4">Prodotto</th>
            <th className="py-3 pr-4">Cliente</th><th className="py-3 pr-4">Importo</th><th className="py-3">Tipo</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-line/60" data-testid={`order-row-${o.id}`}>
              <td className="py-3 pr-4 text-mute">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
              <td className="py-3 pr-4 text-ivory">{o.product_name}</td>
              <td className="py-3 pr-4 text-mute">
                {o.email}
                {o.billing && (
                  <span className="block text-xs text-mist" data-testid={`order-billing-${o.id}`}>
                    Fattura: {o.billing.nome} — CF {o.billing.codice_fiscale}{o.billing.piva ? ` — P.IVA ${o.billing.piva}` : ""}, {o.billing.indirizzo}, {o.billing.cap} {o.billing.citta} ({o.billing.provincia})
                  </span>
                )}
              </td>
              <td className="py-3 pr-4 text-ivory">{formatPrice(o.amount, o.currency)}</td>
              <td className="py-3 text-mute">{o.type}</td>
            </tr>
          ))}
          {orders.length === 0 && (
            <tr><td colSpan={5} className="py-8 text-center text-mute" data-testid="orders-empty">Nessun ordine ancora</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  useEffect(() => { api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-4" data-testid="admin-messages-tab">
      {messages.map((m) => (
        <div key={m.id} className="border border-line p-6" data-testid={`message-row-${m.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-ivory"><strong>{m.name}</strong> <span className="text-mute">· {m.email}</span></p>
            <p className="text-xs text-mute">{m.topic} · {new Date(m.created_at).toLocaleDateString("it-IT")}</p>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-mute">{m.message}</p>
        </div>
      ))}
      {messages.length === 0 && <p className="py-8 text-center text-mute" data-testid="messages-empty">Nessun messaggio ancora</p>}
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-4" data-testid="admin-bookings-tab">
      {bookings.map((b) => (
        <div key={b.id} className="border border-line p-6" data-testid={`booking-row-${b.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-ivory"><strong>{b.name}</strong> <span className="text-mute">· {b.email}{b.phone ? ` · ${b.phone}` : ""}</span></p>
            <p className="text-xs text-mist">{b.date} alle {b.time}</p>
          </div>
          {b.notes && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-mute">{b.notes}</p>}
        </div>
      ))}
      {bookings.length === 0 && <p className="py-8 text-center text-mute" data-testid="bookings-empty">Nessuna prenotazione ancora</p>}
    </div>
  );
}

const CALL_SLOTS = ["09:00", "10:00", "11:00", "12:00", "14:30", "15:30", "16:30", "17:30", "18:30"];

function AvailabilityTab() {
  const [blocked, setBlocked] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blocked-slots").then((r) => setBlocked(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!date) return;
    setSaving(true);
    try {
      await api.post("/admin/blocked-slots", { date, time: time || null });
      toast.success(time ? `Bloccato ${date} alle ${time}` : `Giornata ${date} bloccata`);
      setDate("");
      setTime("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blocked-slots/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-availability-tab">
      <h2 className="mb-2 font-display text-2xl text-ivory">Disponibilità call</h2>
      <p className="mb-8 text-sm text-mute">Blocca i giorni o gli orari in cui non sei disponibile: non saranno più selezionabili nella pagina di prenotazione. Gli orari già prenotati risultano occupati automaticamente.</p>
      <div className="mb-10 flex flex-wrap items-end gap-4 border border-line p-6">
        <div>
          <label className="label-caps mb-2 block text-ivory/60" htmlFor="block-date">Giorno</label>
          <input id="block-date" type="date" className="input-dark bg-ink" data-testid="block-date-input"
            value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label-caps mb-2 block text-ivory/60" htmlFor="block-time">Orario</label>
          <select id="block-time" className="input-dark bg-ink" data-testid="block-time-select"
            value={time} onChange={(e) => setTime(e.target.value)}>
            <option value="">Tutto il giorno</option>
            {CALL_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={add} disabled={saving || !date} data-testid="block-add-button" className="btn-primary !px-6 !py-3 disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : "Blocca"}
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {blocked.map((b) => (
          <div key={b.id} className="flex items-center justify-between border border-line px-6 py-4" data-testid={`blocked-row-${b.id}`}>
            <p className="text-sm text-ivory">
              {b.date} — <span className="text-mist">{b.time || "tutto il giorno"}</span>
            </p>
            <button onClick={() => remove(b.id)} data-testid={`blocked-remove-${b.id}`} className="label-caps text-mute transition-colors duration-300 hover:text-ivory">
              Sblocca
            </button>
          </div>
        ))}
        {blocked.length === 0 && <p className="py-6 text-center text-sm text-mute" data-testid="blocked-empty">Nessun blocco attivo</p>}
      </div>
    </div>
  );
}

function OnboardingTab() {
  const [forms, setForms] = useState([]);
  useEffect(() => { api.get("/admin/onboarding").then((r) => setForms(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-4" data-testid="admin-onboarding-tab">
      {forms.map((f) => (
        <div key={f.id} className="border border-line p-6" data-testid={`onboarding-row-${f.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-ivory">
              <strong>{f.name}</strong> <span className="text-mute">· {f.email}</span>
              {f.consent && <span className="ml-2 text-xs text-sage">consenso testimonianza ✓</span>}
            </p>
            <p className="text-xs text-mute">{new Date(f.created_at).toLocaleDateString("it-IT")}</p>
          </div>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            {(f.answers || []).filter((a) => a.answer).map((a, i) => (
              <div key={i}>
                <dt className="text-xs uppercase tracking-widest text-mist">{a.question}</dt>
                <dd className="mt-1 whitespace-pre-wrap text-sm text-mute">{a.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      {forms.length === 0 && <p className="py-8 text-center text-mute" data-testid="onboarding-empty">Nessun questionario ancora</p>}
    </div>
  );
}

const TABS = [["products", "Prodotti"], ["content", "Contenuti"], ["orders", "Ordini"], ["messages", "Messaggi"], ["bookings", "Prenotazioni"], ["availability", "Disponibilità"], ["onboarding", "Questionari"]];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin"));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="animate-spin text-mist" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div>
            <p className="font-display text-lg text-ivory">Dashboard</p>
            <p className="text-xs text-mute">{user.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="label-caps text-mute transition-colors duration-300 hover:text-ivory" data-testid="admin-view-site">Vedi il sito</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="btn-outline !px-5 !py-2.5">
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-10 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`label-caps rounded-full border px-5 py-2.5 transition-colors duration-300 ${
                tab === key ? "border-sage bg-sage text-ivory" : "border-line text-mute hover:text-ivory"
              }`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "products" && <ProductsTab />}
        {tab === "content" && <ContentTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "availability" && <AvailabilityTab />}
        {tab === "onboarding" && <OnboardingTab />}
      </div>
    </div>
  );
}
